/**
 * src/shell/gm-client/actions.ts — 玩家写操作（外壳写入边界，设计稿 §5.1.5）。
 *
 * 外壳写存档仅限：招聘/解雇（employees+candidates）、立项（projects）、
 * 拍板（decisions；状态推进 projects→developing）、设置/导入导出。
 * 引擎驱动一律走 enqueue（唤醒请求），瞬态门禁（事故三选/上架确认）直接入队。
 * GmError 的 message 是游戏世界观文案，直接上浮 toast（技术细节只在控制台）。
 */

import {
  GmError,
  calcCost,
  type Decision,
  type Employee,
  type EmployeeRole,
  type Project,
  type WakeupAction,
} from '../../gm'

/** 选项键（gm index 未单独导出 OptionKey——从 Decision 派生，以 index 导出类型为准） */
type OptionKey = Decision['options'][number]['key']
import { COPY } from '../copy'
import { readCandidates, writeCandidates } from '../save/candidates'
import { localSaveStore } from '../save/store'
import { gm } from './master'
import { pushToast } from './toast'

// ── 错误上浮 ────────────────────────────────────────────────────────────────

function surfaceError(err: unknown): void {
  if (err instanceof GmError) pushToast(`${COPY.shell.errorPrefix}${err.message}`)
  else {
    console.error('[shell] 未预期错误：', err)
    pushToast(COPY.shell.resumeFail)
  }
}

/** enqueue 包装：非法动作/执行失败的 GmError 上浮 toast */
async function enqueueSafe(action: WakeupAction, projectId: string): Promise<void> {
  try {
    await gm.enqueue(action, projectId)
  } catch (err) {
    surfaceError(err)
  }
}

// ── 人事（设计稿 §2 Phase 1；每岗在职上限 1 人——GDD §2.1）─────────────────

export async function hireCandidate(candidateId: string): Promise<void> {
  const candidates = readCandidates()
  const cand = candidates.find((c) => c.id === candidateId)
  if (!cand) return
  const bundle = await localSaveStore.readAll()
  const employees = bundle.employees.employees
  if (employees.some((e) => e.role === cand.role)) {
    pushToast(COPY.phone.hrDept.roleFull)
    return
  }
  // 单一失败域（M4 整改，质量报告 §一·1.1 第 5 条）：先写 employees 批次——
  // 批次失败则候选仍在架（人才不蒸发，v2 无候选补货机制）；批次成功后候选才出架。
  // 反向残余窗口（出架失败→员工在职+候选残留）只造成重复，不造成丢失，且
  // writeCandidates 为单键 setItem 天然原子，实际几乎不可触发。
  try {
    await localSaveStore.writeBatch({
      employees: { employees: [...employees, { ...cand, status: 'hired' as const }] },
    })
  } catch (err) {
    console.error('[shell] 招聘写入失败（候选已保留在架）：', err)
    pushToast(COPY.shell.writeFail)
    return
  }
  writeCandidates(candidates.filter((c) => c.id !== candidateId))
  pushToast(COPY.phone.hrDept.hiredToast(cand.name))
}

export async function fireEmployee(employeeId: string): Promise<void> {
  const bundle = await localSaveStore.readAll()
  const emp = bundle.employees.employees.find((e) => e.id === employeeId)
  if (!emp || emp.status !== 'hired') return // 在项目中不可解雇
  await localSaveStore.writeBatch({
    employees: { employees: bundle.employees.employees.filter((e) => e.id !== employeeId) },
  })
}

// ── 立项（设计稿 §2 Phase 2；团队 = 在岗三人自动成队）───────────────────────

export type TeamReadiness =
  | { ok: true; team: Project['team']; members: Employee[] }
  | { ok: false; missing: EmployeeRole[] }

export async function teamReadiness(): Promise<TeamReadiness> {
  const bundle = await localSaveStore.readAll()
  const hired = bundle.employees.employees.filter((e) => e.status !== 'candidate')
  const roles: EmployeeRole[] = ['designer', 'programmer', 'artist']
  const missing = roles.filter((r) => !hired.some((e) => e.role === r))
  if (missing.length > 0) return { ok: false, missing }
  const members = roles.map((r) => hired.find((e) => e.role === r)!)
  return {
    ok: true,
    team: { designer: members[0].id, programmer: members[1].id, artist: members[2].id },
    members,
  }
}

/** 成本预估（运行时读 market 参数——数值只有一个家；引擎公式复用） */
export async function estimateCost(team: Project['team']): Promise<{ coins: number; tokens: number }> {
  const bundle = await localSaveStore.readAll()
  const coins = calcCost(team, bundle.employees.employees, bundle.market)
  // token 量级 = 成本反算（每 token_to_coin_rate token = 1 金币，GDD §4.1）
  return { coins, tokens: Math.round(coins * bundle.market.token_to_coin_rate) }
}

export async function createProject(idea: string): Promise<Project | null> {
  const trimmed = idea.trim()
  if (!trimmed) {
    pushToast(COPY.newProject.ideaRequired)
    return null
  }
  const readiness = await teamReadiness()
  if (!readiness.ok) {
    pushToast(COPY.newProject.teamMissing)
    return null
  }
  const bundle = await localSaveStore.readAll()
  const project: Project = {
    id: `proj_${Date.now().toString(36)}`,
    idea: trimmed,
    target_genre: 'other_simple', // 极简模式：体裁由策划裁剪，立项不另问（设计稿 §2.3 一句话输入）
    team: readiness.team,
    status: 'drafting',
    decision_ids: [],
    review_id: null,
    output_path: null,
    token_usage: { designer: 0, programmer: 0, artist: 0, total: 0 },
    created_at: new Date().toISOString(),
  }
  await localSaveStore.writeBatch({
    projects: { projects: [...bundle.projects.projects, project] },
  })
  return project
}

// ── 引擎驱动（enqueue 唯一入口）────────────────────────────────────────────

export const startPlanning = (projectId: string) => enqueueSafe('startPlanning', projectId)
export const publishProject = (projectId: string) => enqueueSafe('publish', projectId)
export const abandonProject = (projectId: string) => enqueueSafe('abandon', projectId)
/** 事故三选（瞬态门禁 = 唤醒请求直接入队，引擎 §8.3） */
export const accidentRetry = (projectId: string) => enqueueSafe('startDevelopment', projectId)
export const accidentDefective = (projectId: string) => enqueueSafe('publishDefective', projectId)

// ── 拍板（设计稿 §2 Phase 3；决策类拍板 = 端口写回 + enqueue 续跑，引擎 §8.3）──

export async function chooseOption(decision: Decision, optionKey: OptionKey): Promise<void> {
  const bundle = await localSaveStore.readAll()
  const decisions = bundle.decisions.decisions.map((d) =>
    d.id === decision.id ? { ...d, player_choice: optionKey, status: 'resolved' as const } : d,
  )
  await localSaveStore.writeBatch({ decisions: { decisions } })
}

/** 全部拍板后「开始开发」：写回状态推进 + enqueue 驱动引擎（批量原子两键） */
export async function confirmDevelopment(projectId: string): Promise<void> {
  const bundle = await localSaveStore.readAll()
  const project = bundle.projects.projects.find((p) => p.id === projectId)
  if (!project || project.status !== 'awaiting_decision') return
  const projects = bundle.projects.projects.map((p) =>
    p.id === projectId ? { ...p, status: 'developing' as const } : p,
  )
  await localSaveStore.writeBatch({ projects: { projects } })
  await enqueueSafe('startDevelopment', projectId)
}
